import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  Leaf,
  User,
  Target,
  Users,
  LogOut,
  FileDown,
  Edit,
  Award,
  TrendingUp,
  Calendar,
  Shield,
  ChevronRight,
  MessageSquare,
  CheckCircle,
  Clock,
  Send
} from "lucide-react";

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [survey, setSurvey] = useState(null);
  const [matches, setMatches] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [surveyRes, matchesRes, conversationsRes] = await Promise.all([
        axios.get(`${API}/survey`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/matches`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/conversations`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ]);
      setSurvey(surveyRes.data);
      setMatches(matchesRes.data || []);
      setConversations(conversationsRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedConversation) return;
    
    setSendingReply(true);
    try {
      await axios.post(
        `${API}/conversations`,
        {
          coach_id: selectedConversation.coach_id,
          message: replyText,
          conversation_id: selectedConversation.id
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Message sent!");
      setReplyText("");
      fetchData(); // Refresh conversations
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSendingReply(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success("Logged out successfully");
  };

  const generatePDF = () => {
    if (!survey) {
      toast.error("Please complete the survey first");
      return;
    }

    const surveyData = survey.survey_data;
    const content = `
COACHBRIDGE COACHING PROFILE
===========================
Generated: ${new Date().toLocaleDateString()}

PERSONAL INFORMATION
--------------------
Name: ${user.first_name} ${user.last_name}
Email: ${user.email}
Age Range: ${surveyData.demographics?.age_range || "Not specified"}
Location: ${surveyData.demographics?.location || "Not specified"}
Profession: ${surveyData.demographics?.profession || "Not specified"}
Industry: ${surveyData.demographics?.industry || "Not specified"}

CORE VALUES
-----------
${surveyData.values?.core_values?.join(", ") || "Not specified"}

WORK VALUES
-----------
${surveyData.values?.work_values?.join(", ") || "Not specified"}

STRONG SKILLS
-------------
${surveyData.skills?.strong_skills?.join(", ") || "Not specified"}

DEVELOPING SKILLS
-----------------
${surveyData.skills?.developing_skills?.join(", ") || "Not specified"}

CHALLENGE AREAS
---------------
${surveyData.weaknesses?.challenge_areas?.join(", ") || "Not specified"}

COACHING INTERESTS
------------------
${surveyData.goals?.coaching_categories?.join(", ") || "Not specified"}

SPECIFIC GOALS
--------------
${surveyData.goals?.specific_goals || "Not specified"}

TIMELINE
--------
${surveyData.goals?.timeline || "Not specified"}

COACH PREFERENCES
-----------------
Gender Preference: ${surveyData.preferences?.gender_preference || "No preference"}
Experience Level: ${surveyData.preferences?.experience_level || "Any"}
Coaching Style: ${surveyData.preferences?.coaching_style?.join(", ") || "Not specified"}
Communication: ${surveyData.preferences?.communication_preference?.join(", ") || "Not specified"}
Availability: ${surveyData.preferences?.availability || "Flexible"}
    `;

    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `bloommatch-profile-${user.first_name.toLowerCase()}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success("Profile downloaded successfully!");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex flex-col items-center justify-center gap-4">
        <div className="w-12 h-12 border-4 border-[#7C9A92]/30 border-t-[#7C9A92] rounded-full animate-spin"></div>
        <div className="text-[#7C9A92] text-xl">Matching you with the right coach...</div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#5C6B5A] text-lg mb-4">Something went wrong loading your dashboard.</p>
          <button onClick={() => { setLoadError(false); setLoading(true); fetchData(); }} className="px-4 py-2 bg-[#7C9A92] text-white rounded-lg hover:bg-[#6B8980]">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Leaf className="w-8 h-8 text-[#7C9A92]" />
            <span className="font-serif text-2xl font-medium text-[#2C332B]">CoachBridge</span>
          </Link>
          
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/dashboard" className="text-[#7C9A92] font-medium">Dashboard</Link>
            <Link to="/coaches" className="text-[#78716C] hover:text-[#2C332B] font-medium">Coaches</Link>
            {user?.is_admin && (
              <Link to="/admin" className="text-[#78716C] hover:text-[#2C332B] font-medium flex items-center gap-1">
                <Shield className="w-4 h-4" />
                Admin
              </Link>
            )}
          </nav>

          <div className="flex items-center gap-4">
            <span className="hidden md:block text-[#78716C]">
              Welcome, {user?.first_name}
            </span>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="text-[#78716C] hover:text-[#2C332B]"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-12">
        {/* Welcome Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h1 className="font-serif text-4xl md:text-5xl font-light text-[#2C332B] mb-4">
            Welcome back, {user?.first_name}
          </h1>
          <p className="text-lg text-[#78716C]">
            {survey 
              ? "Here's your coaching profile and recommended matches." 
              : "Complete your survey to get matched with coaches."}
          </p>
        </motion.div>

        {/* Quick Actions */}
        {!survey && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-12"
          >
            <Card className="bg-gradient-to-r from-[#7C9A92]/10 to-[#E09F7D]/10 border-none">
              <CardContent className="p-8 flex flex-col md:flex-row items-center justify-between gap-6">
                <div>
                  <h2 className="font-serif text-2xl text-[#2C332B] mb-2">
                    Complete Your Profile
                  </h2>
                  <p className="text-[#78716C]">
                    Take our quick survey to get matched with the perfect coach for your journey.
                  </p>
                </div>
                <Button
                  onClick={() => navigate("/survey")}
                  className="rounded-full bg-[#7C9A92] hover:bg-[#6B8A82] text-white px-8 py-6 whitespace-nowrap"
                  data-testid="start-survey-btn"
                >
                  Start Survey
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Bento Grid Dashboard */}
        <div className="bento-grid">
          {/* Profile Summary Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="lg:col-span-2"
          >
            <Card className="h-full">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-serif text-xl flex items-center gap-2">
                  <User className="w-5 h-5 text-[#7C9A92]" />
                  Your Profile
                </CardTitle>
                {survey && (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate("/survey")}
                      className="rounded-full"
                      data-testid="edit-survey-btn"
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={generatePDF}
                      className="rounded-full"
                      data-testid="download-profile-btn"
                    >
                      <FileDown className="w-4 h-4 mr-1" />
                      Download
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                {survey ? (
                  <div className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-[#78716C] mb-1">Location</p>
                        <p className="font-medium text-[#2C332B]">
                          {survey.survey_data.demographics?.location || "Not specified"}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-[#78716C] mb-1">Profession</p>
                        <p className="font-medium text-[#2C332B]">
                          {survey.survey_data.demographics?.profession || "Not specified"}
                        </p>
                      </div>
                    </div>
                    
                    <div>
                      <p className="text-sm text-[#78716C] mb-2">Core Values</p>
                      <div className="flex flex-wrap gap-2">
                        {survey.survey_data.values?.core_values?.map((value) => (
                          <Badge key={value} variant="secondary" className="bg-[#F5F1E8] text-[#2C332B]">
                            {value}
                          </Badge>
                        )) || <span className="text-[#78716C]">Not specified</span>}
                      </div>
                    </div>

                    <div>
                      <p className="text-sm text-[#78716C] mb-2">Strong Skills</p>
                      <div className="flex flex-wrap gap-2">
                        {survey.survey_data.skills?.strong_skills?.map((skill) => (
                          <Badge key={skill} variant="outline" className="border-[#7C9A92] text-[#7C9A92]">
                            {skill}
                          </Badge>
                        )) || <span className="text-[#78716C]">Not specified</span>}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-[#78716C]">
                    <User className="w-12 h-12 mx-auto mb-4 text-stone-300" />
                    <p>Complete the survey to see your profile</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Coaching Goals Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="h-full">
              <CardHeader>
                <CardTitle className="font-serif text-xl flex items-center gap-2">
                  <Target className="w-5 h-5 text-[#E09F7D]" />
                  Your Goals
                </CardTitle>
              </CardHeader>
              <CardContent>
                {survey?.survey_data?.goals?.coaching_categories?.length > 0 ? (
                  <div className="space-y-4">
                    <div className="flex flex-wrap gap-2">
                      {survey.survey_data.goals.coaching_categories.map((cat) => (
                        <Badge key={cat} className="bg-[#E09F7D]/10 text-[#E09F7D] border-none">
                          {cat}
                        </Badge>
                      ))}
                    </div>
                    {survey.survey_data.goals.timeline && (
                      <div className="flex items-center gap-2 text-sm text-[#78716C]">
                        <Calendar className="w-4 h-4" />
                        {survey.survey_data.goals.timeline}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4 text-[#78716C]">
                    <Target className="w-10 h-10 mx-auto mb-2 text-stone-300" />
                    <p className="text-sm">Set your goals in the survey</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Matched Coaches Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="lg:col-span-2"
          >
            <Card className="h-full">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-serif text-xl flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#7C9A92]" />
                  Recommended Coaches
                </CardTitle>
                <Button
                  variant="link"
                  onClick={() => navigate("/coaches")}
                  className="text-[#7C9A92]"
                  data-testid="view-all-coaches-btn"
                >
                  View All
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </CardHeader>
              <CardContent>
                {matches.length > 0 ? (
                  <div className="grid md:grid-cols-2 gap-4">
                    {matches.slice(0, 4).map((coach) => (
                      <div
                        key={coach.id}
                        onClick={() => navigate("/coaches", { state: { connectToCoachId: coach.id } })}
                        className="flex flex-col gap-3 p-4 rounded-xl border border-stone-100 hover:border-[#7C9A92]/30 transition-all cursor-pointer"
                        data-testid={`matched-coach-${coach.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-full bg-[#F5F1E8] flex-shrink-0 flex items-center justify-center overflow-hidden">
                            {coach.photo_url ? (
                              <img src={coach.photo_url} alt={coach.first_name} className="w-full h-full object-cover" />
                            ) : (
                              <User className="w-6 h-6 text-[#7C9A92]" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-medium text-[#2C332B]">
                                {coach.first_name} {coach.last_name}
                              </p>
                              {coach.is_preferred && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-[#E09F7D]/15 text-[#E09F7D] font-medium">
                                  Preferred
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-[#78716C] truncate">
                              {coach.specializations?.slice(0, 2).join(", ")}
                            </p>
                          </div>
                          {coach.match_score && (
                            <div className="match-badge flex-shrink-0">
                              {coach.match_score}%
                            </div>
                          )}
                        </div>
                        {coach.match_reason && (
                          <p className="text-xs text-[#78716C] leading-relaxed line-clamp-3 border-t border-stone-100 pt-2">
                            {coach.match_reason}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-[#78716C]">
                    <Users className="w-12 h-12 mx-auto mb-4 text-stone-300" />
                    <p>{survey ? "No coaches available yet" : "Complete the survey to see matches"}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Growth Areas Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="h-full">
              <CardHeader>
                <CardTitle className="font-serif text-xl flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-[#7C9A92]" />
                  Growth Areas
                </CardTitle>
              </CardHeader>
              <CardContent>
                {survey?.survey_data?.weaknesses?.challenge_areas?.length > 0 ? (
                  <div className="space-y-3">
                    {survey.survey_data.weaknesses.challenge_areas.slice(0, 4).map((area) => (
                      <div
                        key={area}
                        className="flex items-center gap-3 p-3 rounded-lg bg-[#F5F1E8]"
                      >
                        <div className="w-2 h-2 rounded-full bg-[#E09F7D]" />
                        <span className="text-sm font-medium text-[#2C332B]">{area}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 text-[#78716C]">
                    <TrendingUp className="w-10 h-10 mx-auto mb-2 text-stone-300" />
                    <p className="text-sm">Identify growth areas in the survey</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* My Conversations Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="lg:col-span-2"
          >
            <Card className="h-full">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-serif text-xl flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-[#7C9A92]" />
                  My Conversations
                  {conversations.filter(c => c.unread_by_user).length > 0 && (
                    <Badge className="ml-2 bg-[#7C9A92] text-white">
                      {conversations.filter(c => c.unread_by_user).length} new
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {conversations.length > 0 ? (
                  <div className="space-y-4">
                    {conversations.slice(0, 4).map((conv) => (
                      <div
                        key={conv.id}
                        onClick={() => setSelectedConversation(conv)}
                        className={`p-4 rounded-xl border cursor-pointer hover:shadow-md transition-all ${
                          conv.unread_by_user 
                            ? "border-[#7C9A92] bg-[#7C9A92]/5" 
                            : "border-stone-100 bg-white hover:border-[#7C9A92]/30"
                        }`}
                        data-testid={`conversation-${conv.id}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-[#F5F1E8] flex items-center justify-center">
                              <User className="w-4 h-4 text-[#7C9A92]" />
                            </div>
                            <div>
                              <p className="font-medium text-[#2C332B] text-sm">{conv.coach_name}</p>
                              <p className="text-xs text-[#78716C]">
                                {conv.messages?.length || 0} messages
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <Badge 
                              variant="secondary"
                              className={conv.unread_by_user ? "bg-[#7C9A92] text-white" : "bg-stone-100"}
                            >
                              {conv.messages?.[conv.messages.length - 1]?.sender_type === "coach" ? "New Reply" : "Sent"}
                            </Badge>
                            <p className="text-xs text-[#78716C] mt-1">
                              {new Date(conv.updated_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        
                        <p className="text-sm text-[#78716C] line-clamp-1">
                          {conv.last_message_preview}
                        </p>
                      </div>
                    ))}
                    {conversations.length > 4 && (
                      <p className="text-center text-sm text-[#78716C]">
                        + {conversations.length - 4} more conversations
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 text-[#78716C]">
                    <MessageSquare className="w-12 h-12 mx-auto mb-4 text-stone-300" />
                    <p>No conversations yet</p>
                    <p className="text-sm mt-2">
                      Connect with a coach to start a conversation
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Conversation Dialog */}
        <Dialog open={!!selectedConversation} onOpenChange={() => setSelectedConversation(null)}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-[#7C9A92]" />
                Conversation with {selectedConversation?.coach_name}
              </DialogTitle>
            </DialogHeader>
            
            {selectedConversation && (
              <div className="space-y-4 mt-4">
                {/* Messages Thread */}
                <div className="space-y-3 max-h-[400px] overflow-y-auto p-2">
                  {selectedConversation.messages?.map((msg) => (
                    <div
                      key={msg.id}
                      className={`p-3 rounded-xl ${
                        msg.sender_type === "user" 
                          ? "bg-[#F5F1E8] ml-8" 
                          : "bg-[#7C9A92]/10 mr-8"
                      }`}
                    >
                      <div className="flex justify-between items-start mb-1">
                        <p className="text-xs font-medium text-[#7C9A92]">
                          {msg.sender_type === "user" ? "You" : selectedConversation.coach_name}
                        </p>
                        <p className="text-xs text-[#78716C]">
                          {new Date(msg.sent_at).toLocaleString()}
                        </p>
                      </div>
                      <p className="text-sm text-[#2C332B]">{msg.message}</p>
                    </div>
                  ))}
                </div>

                {/* Reply Form */}
                <div className="border-t pt-4">
                  <Textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Type your message..."
                    className="min-h-[80px] rounded-xl mb-3"
                    data-testid="conversation-reply-input"
                  />
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setSelectedConversation(null)}
                      className="rounded-full"
                    >
                      Close
                    </Button>
                    <Button
                      onClick={handleSendReply}
                      disabled={sendingReply || !replyText.trim()}
                      className="rounded-full bg-[#7C9A92] hover:bg-[#6B8A82]"
                      data-testid="send-conversation-reply-btn"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      {sendingReply ? "Sending..." : "Send"}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default DashboardPage;
